import { Component } from '@theme/component';

/**
 * A real-deadline countdown. Never resets and never fakes time remaining:
 * once the configured end datetime has passed, it hides itself or swaps to
 * the expired message, depending on `data-expired-behavior`.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [running] - Wrapper shown while time remains.
 * @property {HTMLElement} [expired] - Wrapper shown once expired.
 * @property {HTMLElement} [time] - The element the countdown digits render into.
 *
 * @extends {Component<Refs>}
 */
class AvCountdown extends Component {
  /** @type {number | undefined} */
  #intervalId;

  /** @type {number} */
  #endTime = NaN;

  connectedCallback() {
    super.connectedCallback();
    clearInterval(this.#intervalId);

    const endDatetime = this.dataset.endDatetime;
    this.#endTime = endDatetime ? Date.parse(endDatetime) : NaN;

    if (Number.isNaN(this.#endTime)) {
      // No valid, real deadline: never fabricate a countdown.
      this.hidden = true;
      return;
    }

    this.#tick();
    this.#intervalId = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#intervalId);
  }

  #tick() {
    const remainingMs = this.#endTime - Date.now();

    if (remainingMs <= 0) {
      clearInterval(this.#intervalId);
      this.#showExpired();
      return;
    }

    const { time } = this.refs;
    if (!time) return;

    const totalSeconds = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (/** @type {number} */ value) => String(value).padStart(2, '0');

    time.textContent = days > 0 ? `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  #showExpired() {
    const { running, expired } = this.refs;

    if (this.dataset.expiredBehavior === 'text') {
      if (running) running.hidden = true;
      if (expired) expired.hidden = false;
    } else {
      this.hidden = true;
    }
  }
}

if (!customElements.get('av-countdown')) {
  customElements.define('av-countdown', AvCountdown);
}
